<script setup lang="ts">
import type { Toast } from '@/stores/ui';
import ToastProgress from './ToastProgress.vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

interface Props {
    toast: Toast;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    dismiss: [id: string];
}>();

// ---------------------------------------------------------------------------
// Timer management — pause on hover, resume on leave
// ---------------------------------------------------------------------------
const isPaused = ref(false);
const progressKey = ref(0);
let remaining = 0;
let startTime = 0;
let timerId: ReturnType<typeof setTimeout> | null = null;

const shouldAutoClose = computed(() => {
    return (
        !props.toast.persistent &&
        props.toast.duration > 0 &&
        props.toast.type !== 'loading'
    );
});

function initTimer() {
    clearTimer();
    if (!shouldAutoClose.value) return;
    remaining = props.toast.duration;
    startTimer();
}

function startTimer() {
    startTime = Date.now();
    timerId = setTimeout(() => {
        emit('dismiss', props.toast.id);
    }, remaining);
}

function clearTimer() {
    if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
    }
}

function pause() {
    if (!shouldAutoClose.value || timerId === null) return;
    clearTimer();
    remaining -= Date.now() - startTime;
    if (remaining <= 0) remaining = 0;
    isPaused.value = true;
}

function resume() {
    if (!shouldAutoClose.value || remaining <= 0) return;
    isPaused.value = false;
    startTimer();
}

// ---------------------------------------------------------------------------
// Swipe-to-dismiss
// ---------------------------------------------------------------------------
const toastRef = ref<HTMLElement | null>(null);
const swipeOffset = ref(0);
const isSwiping = ref(false);
const isSwipeDismissing = ref(false);
const hasSwipeTransform = ref(false);
let swipeStartX = 0;

const swipeState = computed<string | undefined>(() => {
    if (isSwiping.value) return 'active';
    if (hasSwipeTransform.value) return 'settling';
    return undefined;
});

const toastStyle = computed(() => {
    const style: Record<string, string> = {};

    if (props.toast.color) {
        style['--toast-glow'] = props.toast.color;
    }

    if (hasSwipeTransform.value) {
        style.transform = `translateX(${swipeOffset.value}px)`;
        if (isSwipeDismissing.value) {
            style.opacity = '0';
        } else if (swipeOffset.value > 0) {
            const width = toastRef.value?.offsetWidth ?? 360;
            style.opacity = String(
                Math.max(0.4, 1 - (swipeOffset.value / width) * 0.6)
            );
        }
    }

    return style;
});

function onPointerDown(e: PointerEvent) {
    if (e.button !== 0 || !props.toast.dismissible) return;
    swipeStartX = e.clientX;
    isSwiping.value = true;
    hasSwipeTransform.value = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
}

function onPointerMove(e: PointerEvent) {
    if (!isSwiping.value) return;
    swipeOffset.value = Math.max(0, e.clientX - swipeStartX);
}

function onPointerUp() {
    if (!isSwiping.value) return;
    isSwiping.value = false;

    const width = toastRef.value?.offsetWidth ?? 360;
    if (swipeOffset.value > width * 0.4) {
        isSwipeDismissing.value = true;
        swipeOffset.value = width + 20;
        setTimeout(() => {
            emit('dismiss', props.toast.id);
        }, 300);
    } else {
        swipeOffset.value = 0;
        setTimeout(() => {
            hasSwipeTransform.value = false;
        }, 350);
    }
}

// ---------------------------------------------------------------------------
// Watch for type changes (promise toasts: loading → success/error)
// ---------------------------------------------------------------------------
watch(
    () => props.toast.type,
    () => {
        progressKey.value++;
        isPaused.value = false;
        initTimer();
    }
);

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------
onMounted(() => {
    initTimer();
});

onUnmounted(() => {
    clearTimer();
});
</script>

<template>
    <div
        ref="toastRef"
        data-slot="toast"
        :class="['toast', `toast--${toast.type}`]"
        :style="toastStyle"
        :data-swiping="swipeState"
        :role="toast.type === 'error' ? 'alert' : 'status'"
        :aria-live="toast.type === 'error' ? 'assertive' : 'polite'"
        aria-atomic="true"
        @mouseenter="pause"
        @mouseleave="resume"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
    >
        <!-- Type icon -->
        <div class="toast__icon" aria-hidden="true">
            <svg
                v-if="toast.type === 'success'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
            </svg>

            <svg
                v-else-if="toast.type === 'error'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <path d="m15 9-6 6" />
                <path d="m9 9 6 6" />
            </svg>

            <svg
                v-else-if="toast.type === 'warning'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path
                    d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
                />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
            </svg>

            <svg
                v-else-if="toast.type === 'info'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
            </svg>

            <svg
                v-else-if="toast.type === 'loading'"
                class="toast__spinner"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
            >
                <circle cx="12" cy="12" r="10" opacity="0.2" />
                <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
        </div>

        <!-- Content -->
        <div class="toast__body">
            <p class="toast__title">{{ toast.title }}</p>
            <p v-if="toast.message" class="toast__message">
                {{ toast.message }}
            </p>
            <div v-if="toast.action" class="toast__actions">
                <button
                    class="toast__action"
                    @click.stop="toast.action.onClick"
                >
                    {{ toast.action.label }}
                </button>
            </div>
        </div>

        <!-- Close button -->
        <button
            v-if="toast.dismissible"
            class="toast__close"
            aria-label="Dismiss notification"
            @click.stop="emit('dismiss', toast.id)"
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
            </svg>
        </button>

        <!-- Progress bar -->
        <ToastProgress
            v-if="shouldAutoClose"
            :key="progressKey"
            :duration="toast.duration"
            :paused="isPaused"
        />
    </div>
</template>
